<!--
  GlobalDefaultMargin -- SuperAdmin global-defaults editor for the
  platform-wide default base margin.

  Card: "Distributor app (route fork)" - https://trello.com/c/QV2U4KhF
  Pebble: SA global default base margin (4-level hierarchy root).

  Functional model (locked by Jake 2026-06-04):
    * Layer 3 of the base-margin resolver. The chain is:
        store.base_margin
        ?? distributor.default_base_margin
        ?? platform_settings['default_base_margin']  (this surface)
        ?? 0.40   (hardcoded last-resort in code)
    * NULL here means "no SA default" -- the resolver falls through to
      the hardcoded 0.40.

  Lives on /admin/plans-pricing (PlansPricing.vue), positioned ABOVE
  the existing mock plans table as the first non-mock section on that
  page. Carries its own local "Live" indicator so reviewers know this
  card is real even when the rest of the page is DEMO DATA.

  Endpoint:
    * GET   /api/platform-settings/default-base-margin
        -> {"value": <fraction or null>}
        @login_required only (the value is non-sensitive; inheritance
        hints in distributor/store UIs need it)
    * PATCH /api/platform-settings/default-base-margin
        body {"value": <fraction or null>}
        gated on ``superadmin.settings`` (existing high-watermark cap)

  Shape mirrors DefaultsPricing.vue (percent input + Save + Reset).
  data-testid prefix: global-default-margin-*.
-->
<template>
    <section
        class="global-default-margin"
        data-testid="global-default-margin-root"
    >
        <div class="global-default-margin__header">
            <h2 class="global-default-margin__title">Global default base margin</h2>
            <span
                class="global-default-margin__live-badge"
                data-testid="global-default-margin-live"
                title="This section is backed by the live platform_settings endpoint -- not mock data."
            >
                Live
            </span>
        </div>

        <p class="global-default-margin__lede">
            Applied to every newly-created distributor that has not set its
            own default. Each distributor can override this in their
            Account Defaults; each store can in turn override its
            distributor's default in the store editor.
            Setting this back to blank reverts to ThreadFlow's hardcoded
            platform fallback of 40%.
        </p>

        <v-progress-circular
            v-if="loading"
            :indeterminate="true"
            color="primary"
            size="32"
            width="4"
            class="mt-4"
            data-testid="global-default-margin-loading"
        />

        <div v-else class="global-default-margin__form">
            <label
                class="global-default-margin__label"
                for="global-default-margin-input"
            >
                Default base margin
            </label>
            <div class="global-default-margin__input-row">
                <input
                    id="global-default-margin-input"
                    v-model="percentDraft"
                    type="number"
                    inputmode="decimal"
                    step="1"
                    min="0"
                    max="100"
                    placeholder="40"
                    class="global-default-margin__input"
                    data-testid="global-default-margin-input"
                    @input="onInput"
                />
                <span class="global-default-margin__suffix">%</span>
            </div>
            <p
                v-if="errorMsg"
                class="global-default-margin__error"
                data-testid="global-default-margin-error"
            >
                {{ errorMsg }}
            </p>
            <p
                v-else
                class="global-default-margin__hint"
                data-testid="global-default-margin-hint"
            >
                {{ hint }}
            </p>

            <div class="global-default-margin__actions">
                <button
                    type="button"
                    class="tf-btn tf-btn--primary"
                    :disabled="!canSave"
                    data-testid="global-default-margin-save"
                    @click="save"
                >
                    {{ saving ? "Saving..." : "Save" }}
                </button>
                <button
                    v-if="hasStoredValue"
                    type="button"
                    class="tf-btn tf-btn--ghost"
                    :disabled="saving"
                    data-testid="global-default-margin-reset"
                    @click="resetToHardcoded"
                >
                    Reset to platform fallback
                </button>
            </div>
        </div>
    </section>
</template>

<script>
import { computed, ref, onMounted } from "vue";
import axios from "@/axios";

// The hardcoded last-resort margin -- the floor of the 4-level chain
// when the SA-global is null. Tracks the backend constant
// (_HARDCODED_FALLBACK_MARGIN in api/src/endpoints/distributor_defaults.py).
const HARDCODED_FALLBACK_PERCENT = 40;

const URL = "/api/platform-settings/default-base-margin";

export default {
    name: "GlobalDefaultMargin",

    setup() {
        // Stored fraction in [0, 1] from the backend; null = "no SA
        // default set" -> the resolver falls through to the hardcoded
        // last-resort.
        const storedFraction = ref(null);
        const percentDraft = ref("");
        const loading = ref(true);
        const saving = ref(false);
        const errorMsg = ref("");

        const hasStoredValue = computed(
            () => storedFraction.value !== null
        );

        const hint = computed(() => {
            if (storedFraction.value === null) {
                return `No SA global set. New distributors inherit ThreadFlow's hardcoded fallback (${HARDCODED_FALLBACK_PERCENT}%).`;
            }
            const pct = Math.round(storedFraction.value * 100);
            return `Current global default: ${pct}%. New distributors inherit this value.`;
        });

        const canSave = computed(() => {
            if (saving.value) return false;
            if (errorMsg.value) return false;
            if (percentDraft.value === "") {
                // Save with empty input clears the value -- only
                // valid when something was stored.
                return storedFraction.value !== null;
            }
            const n = Number(percentDraft.value);
            if (!Number.isFinite(n)) return false;
            if (n < 0 || n > 100) return false;
            return true;
        });

        async function load() {
            loading.value = true;
            errorMsg.value = "";
            try {
                const resp = await axios.get(URL);
                const value = resp?.data?.value ?? null;
                storedFraction.value = value;
                percentDraft.value =
                    value === null ? "" : String(Math.round(value * 100));
            } catch (e) {
                errorMsg.value = "Could not load the global default margin.";
            } finally {
                loading.value = false;
            }
        }

        function onInput() {
            errorMsg.value = "";
            if (percentDraft.value === "") return;
            const n = Number(percentDraft.value);
            if (!Number.isFinite(n)) {
                errorMsg.value = "Enter a number between 0 and 100.";
                return;
            }
            if (n < 0 || n > 100) {
                errorMsg.value = "Margin must be between 0 and 100.";
            }
        }

        async function save() {
            saving.value = true;
            errorMsg.value = "";
            const fraction =
                percentDraft.value === ""
                    ? null
                    : Number(percentDraft.value) / 100;
            try {
                const resp = await axios.patch(URL, { value: fraction });
                const next = resp?.data?.value ?? null;
                storedFraction.value = next;
                percentDraft.value =
                    next === null ? "" : String(Math.round(next * 100));
            } catch (e) {
                const status = e?.response?.status;
                if (status === 403) {
                    errorMsg.value =
                        "Permission denied. Only Super Admins can edit the global default.";
                } else {
                    errorMsg.value =
                        "Could not save the global default margin.";
                }
            } finally {
                saving.value = false;
            }
        }

        async function resetToHardcoded() {
            percentDraft.value = "";
            await save();
        }

        onMounted(load);

        return {
            loading,
            saving,
            errorMsg,
            hint,
            percentDraft,
            hasStoredValue,
            canSave,
            onInput,
            save,
            resetToHardcoded,
        };
    },
};
</script>

<style scoped>
.global-default-margin {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px 20px;
    margin-bottom: 24px;
}
.global-default-margin__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
}
.global-default-margin__title {
    font-size: 18px;
    font-weight: 600;
    color: #1a202c;
    margin: 0;
}
.global-default-margin__live-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    background: #c6f6d5;
    color: #22543d;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.global-default-margin__lede {
    margin: 0 0 16px;
    color: #4b5563;
    max-width: 640px;
    line-height: 1.5;
    font-size: 14px;
}
.global-default-margin__form {
    max-width: 320px;
}
.global-default-margin__label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    color: #1f2937;
    font-size: 14px;
}
.global-default-margin__input-row {
    display: flex;
    align-items: center;
    gap: 8px;
}
.global-default-margin__input {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 1rem;
    max-width: 120px;
}
.global-default-margin__suffix {
    color: #4b5563;
    font-size: 1rem;
}
.global-default-margin__hint {
    margin-top: 8px;
    color: #4b5563;
    font-size: 0.875rem;
}
.global-default-margin__error {
    margin-top: 8px;
    color: #b91c1c;
    font-size: 0.875rem;
}
.global-default-margin__actions {
    margin-top: 16px;
    display: flex;
    gap: 8px;
}
.tf-btn {
    border: 1px solid transparent;
    border-radius: 6px;
    padding: 8px 20px;
    font-size: 1rem;
    cursor: pointer;
}
.tf-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
.tf-btn--primary {
    background: #1f2937;
    color: #fff;
}
.tf-btn--ghost {
    background: transparent;
    color: #1f2937;
    border-color: #d1d5db;
}
</style>
